import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { User } from '../types';
import { hasActiveBusinessRoles, isPlatformSystemAdmin } from '../utils/roleUtils';
import { Building2, LayoutDashboard, User as UserIcon, LogOut, HelpCircle } from 'lucide-react';
import CustomerHelpModal from './CustomerHelpModal';

interface UserProfileDropdownProps {
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  loginButtonStyle?: React.CSSProperties;
  loginButtonClassName?: string;
  showAvatar?: boolean;
  showDashboardLink?: boolean; // Show "Dashboard" link in dropdown for mobile
  hideName?: boolean; // Hide the user name from the button
  showHelpModal?: boolean; // Show help modal instead of navigating to help page
  /** When set (e.g. theme primary color), avatar uses this as background with white text */
  avatarBrandColor?: string;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  className = '',
  buttonClassName = '',
  dropdownClassName = '',
  loginButtonStyle,
  loginButtonClassName = '',
  showAvatar = true,
  showDashboardLink = false,
  hideName = false,
  showHelpModal = false,
  avatarBrandColor
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current business shortName from URL for redirect
  const businessShortName = location.pathname.split('/')[1];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      // Get stored login type before logout (stored in localStorage)
      const storedLoginType = localStorage.getItem('loginType');
      const wasPlatformAdmin = user ? isPlatformSystemAdmin(user) : false;

      await logout();
      setIsDropdownOpen(false);
      
      // Clear stored login type
      localStorage.removeItem('loginType');
      // Clear business dashboard appointment filters so re-login starts fresh
      try {
        localStorage.removeItem('businessAppointmentsFilters');
        localStorage.removeItem('businessMyAppointmentsFilters');
      } catch {
        // best-effort
      }

      if (wasPlatformAdmin) {
        navigate('/login?type=platform');
        return;
      }
      
      // Redirect based on stored login type or current context
      const path = location.pathname || '';
      
      // Check if we're on a business website (path like /:businessShortName or /:businessShortName/*)
      // Reserved routes that are NOT business websites: dashboard, login, register, search, etc.
      const reservedRoutes = ['dashboard', 'login', 'register', 'search', 'forgot-password', 'reset-password', 'business-registration', 'registration-success', 'account-verification-sent', 'invitation', 'customer-signup', 'verify-email', 'account-settings', 'categories', 'help-support', 'personnel', 'services'];
      const pathParts = path.split('/').filter(Boolean);
      const firstPathSegment = pathParts[0];
      
      // If we're on a business website (first segment is not a reserved route and exists)
      const isOnBusinessWebsite = firstPathSegment && !reservedRoutes.includes(firstPathSegment);
      
      if (isOnBusinessWebsite) {
        // Redirect to business landing page
        navigate(`/${firstPathSegment}`);
      } else {
        // Use stored login type if available, otherwise fall back to context-based detection
        if (storedLoginType === 'professional') {
          navigate('/login?type=professional');
        } else if (storedLoginType === 'business') {
          navigate('/login?type=business');
        } else if (storedLoginType === 'platform' || path.startsWith('/dashboard/platform')) {
          navigate('/login?type=platform');
        } else {
          // Check if we're in business dashboard context
          const isBusinessContext = path.startsWith('/dashboard/business')
            || path.startsWith('/account-settings')
            || path.startsWith('/personnel')
            || path.startsWith('/services')
            || path.startsWith('/categories');
          navigate(isBusinessContext ? '/login?type=business' : '/login?type=customer');
        }
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  const handleDashboardClick = () => {
    if (!user) return;
    if (isPlatformSystemAdmin(user)) {
      navigate('/dashboard/platform');
    } else {
      const goBusiness = hasActiveBusinessRoles(user);
      navigate(goBusiness ? '/dashboard/business' : '/dashboard/customer');
    }
    setIsDropdownOpen(false);
  };

  const handleLoginClick = () => {
    // Navigate to login with redirect back to current business website
    const redirectUrl = businessShortName ? `/${businessShortName}` : '/';
    navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
  };

  // If user is not logged in, show login button
  if (!user) {
    return (
      <div className={className}>
        <button
          onClick={handleLoginClick}
          className={`px-4 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-gray-300 ${buttonClassName} ${loginButtonClassName}`}
          style={{
            backgroundColor: loginButtonStyle?.backgroundColor || '#FFFFFF',
            color: loginButtonStyle?.color || '#111827',
            border: loginButtonStyle?.border || '1px solid #D1D5DB',
            ...loginButtonStyle
          }}
        >
          Sign In
        </button>
      </div>
    );
  }

  const isBusinessUser = user ? hasActiveBusinessRoles(user) : false;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 ${buttonClassName}`}
      >
        {/* User Avatar - Always show on mobile, respect showAvatar prop on desktop */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${showAvatar ? 'block' : 'block md:hidden'}`}
          style={
            avatarBrandColor
              ? { backgroundColor: avatarBrandColor, color: '#fff' }
              : { backgroundColor: '#e5e7eb', color: '#374151' }
          }
        >
          {getUserInitials(user)}
        </div>
        
        {/* User Name */}
        {!hideName && (
          <span className="text-sm font-medium text-gray-900 hidden sm:block">
            {getUserDisplayName(user)}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className={`absolute right-0 mt-2 w-auto min-w-[12rem] rounded-lg shadow-lg py-2 z-50 border border-gray-200 bg-white ${dropdownClassName}`}>
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{getUserDisplayName(user)}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {isBusinessUser && (
              <button
                onClick={() => {
                  navigate('/dashboard/business');
                  setIsDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-3"
              >
                <Building2 className="h-5 w-5 text-gray-600 flex-shrink-0" />
                <span className="whitespace-nowrap">Business Dashboard</span>
              </button>
            )}
            <button
              onClick={() => {
                navigate(isPlatformSystemAdmin(user) ? '/dashboard/platform' : '/dashboard/customer');
                setIsDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-3"
            >
              <LayoutDashboard className="h-5 w-5 text-gray-600 flex-shrink-0" />
              <span className="whitespace-nowrap">My Dashboard</span>
            </button>

            {/* Separator */}
            <div className="my-1 border-t border-gray-200"></div>

            <button
              onClick={() => {
                navigate(isPlatformSystemAdmin(user) ? '/dashboard/platform/profile' : '/dashboard/customer/profile');
                setIsDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-3"
            >
              <UserIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
              <span className="whitespace-nowrap">Profile Settings</span>
            </button>

            <button
              onClick={() => {
                if (showHelpModal) {
                  setIsHelpModalOpen(true);
                } else {
                  navigate('/help-support');
                }
                setIsDropdownOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-3"
            >
              <HelpCircle className="h-5 w-5 text-gray-600 flex-shrink-0" />
              <span className="whitespace-nowrap">Help</span>
            </button>

          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-200 pt-1">
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-3"
            >
              <LogOut className="h-5 w-5 text-gray-600 flex-shrink-0" />
              <span className="whitespace-nowrap">Sign Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <CustomerHelpModal
          isOpen={isHelpModalOpen}
          onClose={() => setIsHelpModalOpen(false)}
        />
      )}
    </div>
  );
};

export default UserProfileDropdown; 